import { useQuery } from "@tanstack/react-query";

interface UsageData {
  tier: string;
  isAdmin: boolean;
  betaMode?: boolean;
  usage: { reports: number; charts: number };
  limits: { reports: number; charts: number; teamAnalysis: boolean; tarot: number; kundali: number };
  canCreateReport: boolean;
  canCreateChart: boolean;
  canUseTeamAnalysis: boolean;
}

export interface OrganizationCoverage {
  id: string;
  name: string;
  plan: "bronze" | "silver" | "gold";
  billingActive: boolean;
  includedOwnerTier: "cosmic" | "celestial";
  ownerUserId: string;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
}
const TIER_LEVELS: Record<string, number> = { free: 0, cosmic: 1, celestial: 2, enterprise: 3 };

const FREE_LIMITS = { reports: -1, charts: 0, teamAnalysis: true, tarot: 0, kundali: 0 };

export function resolveSubscriptionAccess(data: UsageData | undefined) {
  const tier = (data?.tier || "free").toLowerCase();
  const privileged = data?.isAdmin === true || data?.betaMode === true;
  const limits = data?.limits ?? FREE_LIMITS;

  return {
    tier,
    isAdmin: data?.isAdmin === true,
    betaMode: data?.betaMode === true,
    usage: data?.usage,
    limits,
    canCreateReport: privileged || data?.canCreateReport === true,
    canCreateChart: privileged || data?.canCreateChart === true,
    canUseTeamAnalysis: privileged || data?.canUseTeamAnalysis === true,
    hasTier(requiredTier: string): boolean {
      if (privileged) return true;
      return (TIER_LEVELS[tier] ?? 0) >= (TIER_LEVELS[requiredTier.toLowerCase()] ?? 0);
    },
    isPaywallError(error: any): { requiredTier: string; currentTier: string } | null {
      if (privileged || error?.status !== 402 || !error?.requiredTier) return null;
      return {
        requiredTier: String(error.requiredTier),
        currentTier: String(error.currentTier || tier),
      };
    },
  };
}

export function useSubscription() {
  const { data, isLoading } = useQuery<UsageData>({
    queryKey: ['/api/user/usage'],
    retry: false,
  });

  const access = resolveSubscriptionAccess(data);

  return {
    ...access,
    isLoading,
  };
}

export function useOrganizationCoverage(enabled = true) {
  const query = useQuery<OrganizationMemberships>({
    queryKey: ["/api/teams/mine"],
    enabled,
    staleTime: 30_000,
  });
  return { ...query, organization: getActiveOrganization(query.data) };
}

export async function waitForActiveOrganization(
  load: () => Promise<OrganizationMemberships>,
  sleep: (ms: number) => Promise<unknown> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  attempts = 40,
  target?: { orgId?: string | null; plan?: string | null },
): Promise<OrganizationCoverage | null> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const active = getActiveOrganization(await load(), target);
    if (active) return active;
    if (attempt < attempts - 1) await sleep(1500);
  }
  return null;
}

export function getActiveOrganization(
  data: OrganizationMemberships | undefined,
  target?: { orgId?: string | null; plan?: string | null },
) {
  return [...(data?.owned ?? []), ...(data?.memberOf ?? [])].find((org) =>
    org.billingActive &&
    (!target?.orgId || org.id === target.orgId) &&
    (!target?.plan || org.plan === target.plan)
  ) ?? null;
}

export interface OrganizationMemberships {
  owned: Array<OrganizationCoverage & { subscriptionStatus?: string | null }>;
  memberOf: Array<OrganizationCoverage & { subscriptionStatus?: string | null }>;
}
